// Tarjeta individual de solicitud del abogado al cliente
import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircleIcon, ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import type { ExpedienteSolicitudRead } from '@/types/expedienteSolicitud';
import { TIPO_SOLICITUD_LABELS, ESTADO_SOLICITUD_LABELS } from '@/types/expedienteSolicitud';

const ESTADO_COLORS: Record<string, string> = {
  pendiente: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  en_proceso: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  completada: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  cancelada: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

interface ClienteSolicitudCardProps {
  solicitud: ExpedienteSolicitudRead;
  onResponder: (expedienteId: string, solicitudId: string, texto: string, file?: File) => void;
  submitting?: boolean;
}

export function ClienteSolicitudCard({ solicitud, onResponder, submitting }: ClienteSolicitudCardProps) {
  const [texto, setTexto] = useState('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isPending = solicitud.estado === 'pendiente' || solicitud.estado === 'en_proceso';
  const estadoColor = ESTADO_COLORS[solicitud.estado] ?? ESTADO_COLORS['pendiente'];
  const isVencida = solicitud.fecha_limite ? new Date(solicitud.fecha_limite) < new Date() : false;
  const canSubmit = texto.trim() !== '' || file !== undefined;

  const handleSubmit = () => {
    onResponder(solicitud.expediente_id, solicitud.id, texto, file);
  };

  return (
    <Card className="border">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                {TIPO_SOLICITUD_LABELS[solicitud.tipo] ?? solicitud.tipo}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded font-medium ${estadoColor}`}>
                {ESTADO_SOLICITUD_LABELS[solicitud.estado] ?? solicitud.estado}
              </span>
              {isVencida && isPending && (
                <span className="text-xs px-2 py-0.5 rounded font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                  Vencida
                </span>
              )}
            </div>
            <CardTitle className="text-base">{solicitud.titulo}</CardTitle>
          </div>
          {solicitud.estado === 'completada'
            ? <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-1" />
            : isVencida
            ? <ExclamationCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
            : <ClockIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-1" />}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-700 dark:text-gray-300">{solicitud.descripcion}</p>

        {solicitud.fecha_limite && (
          <p className={`text-xs ${isVencida ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-500'}`}>
            Fecha límite: {new Date(solicitud.fecha_limite).toLocaleDateString('es-ES')}
          </p>
        )}

        {solicitud.respuesta_texto && (
          <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded p-2 space-y-1">
            <p className="text-xs font-medium text-green-700 dark:text-green-300">Tu respuesta</p>
            <p className="text-sm text-green-800 dark:text-green-200">{solicitud.respuesta_texto}</p>
            {solicitud.responded_at && (
              <p className="text-xs text-green-600 dark:text-green-400">
                Enviada el {new Date(solicitud.responded_at).toLocaleDateString('es-ES')}
              </p>
            )}
          </div>
        )}

        {isPending && (
          <div className="space-y-2">
            <Textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Escribe tu respuesta aquí..."
              rows={3}
              className="text-sm"
            />
            {solicitud.requiere_archivo && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={(e) => setFile(e.target.files?.[0])}
                />
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
                  Adjuntar archivo
                </Button>
                {file && <span className="ml-2 text-xs text-gray-600">{file.name}</span>}
              </div>
            )}
            <Button size="sm" onClick={handleSubmit} disabled={!canSubmit || submitting}>
              Enviar respuesta
            </Button>
          </div>
        )}

        <p className="text-xs text-gray-400">
          Recibida el {new Date(solicitud.created_at).toLocaleDateString('es-ES', {
            day: 'numeric', month: 'long', year: 'numeric',
          })}
        </p>
      </CardContent>
    </Card>
  );
}
